import React, { FC, useContext, useEffect, useMemo, useRef } from 'react'
import {
	Outlet,
	useLocation,
	useNavigate,
} from 'react-router-dom'
import Navbar from '../common/Navbar'
import AppContext from '../../context/appContext'
import { SignalRContext } from '../../context/signalrContext'
import { CurrentUserObject } from '../../lib/api/types/user.interfaces'
import cn from '../../lib/utils/cn'
import { useAuth } from '../../hooks'
import { useUpdateInboxCache } from '../../hooks/api/inboxHooks'
import { notificationCaller } from '../ui/NotificationToast'
import { Features, useBilling } from '../../context/billingContext'
import { ProgressProvider } from '../../context/progressContext'

interface MainLayoutProps {
	showSidebar: boolean
}
export interface MainLayoutType {
	isAdmin: boolean
	isOwner: boolean
	currentUser: CurrentUserObject
	setCurrentUser: (user: CurrentUserObject) => void
	containerRef: React.RefObject<HTMLDivElement>
	isApp: boolean
}

const MainLayout: FC<MainLayoutProps> = ({ showSidebar }) => {
	const connection = useContext(SignalRContext)?.connection
	const { hasFeature } = useBilling()
	const { updateUnreadCount } = useUpdateInboxCache()
	const location = useLocation()
	const navigate = useNavigate()
	const containerRef = useRef<HTMLDivElement>(null)
	const divRef = useRef<HTMLDivElement>(null)
	const { currentUser, setCurrentUser, isAdmin, isOwner, isAuthenticated } =
		useAuth()
	const { setContainerWidth, isApp } = useContext(AppContext)

	useEffect(() => {
		if (!connection) return
		const onBlocked = () => navigate('/')

		const onInboxNotification = (payload: any) => {
			const notification = payload?.arguments?.[0] || payload
			if (notification) {
				updateUnreadCount(false)
				notificationCaller({ notification })
			}
		}
		connection.on('userBlocked', onBlocked)

		if (hasFeature(Features.Collaboration)) {
			connection.on('inboxNotification', onInboxNotification)
		}

		return () => {
			connection.off('userBlocked', onBlocked)
			connection.off('inboxNotification', onInboxNotification)
		}
	}, [
		connection,
		hasFeature,
		navigate,
		updateUnreadCount,
	])

	useEffect(() => {
		const observer = new ResizeObserver((entries) => {
			if (entries[0]) {
				setContainerWidth(entries[0].contentRect.width)
			}
		})

		if (containerRef.current) {
			observer.observe(containerRef.current)
		}

		return () => {
			observer.disconnect()
		}
	}, [setContainerWidth])

	const isFitHeightPage = ['create-profile', 'settings']?.some((url) =>
		location.pathname.includes(url),
	)

	const outletContext = useMemo(
		() => ({
			isAdmin,
			isOwner,
			currentUser,
			setCurrentUser,
			containerRef,
			isApp,
		}),
		[
			isAdmin,
			isOwner,
			currentUser,
			setCurrentUser,
			isAuthenticated,
			containerRef,
			isApp,
		],
	)

	return (
		<ProgressProvider>
			<div className="box-border" ref={divRef}>
				{showSidebar && (
					<Navbar
						isAdmin={isAdmin}
						isOwner={isOwner}
						currentUser={currentUser}
					/>
				)}
				<div
					className={cn(
						`p-2.5 md:p-5 bg-background-light dark:bg-background-light-dark w-full overflow-x-auto overflow-y-auto box-border`,
						isFitHeightPage
							? 'min-h-[calc(100vh-80px)]'
							: 'h-[calc(100vh-80px)]',
					)}
					ref={containerRef}
				>
					<Outlet context={outletContext} />
				</div>
			</div>
		</ProgressProvider>
	)
}

export default MainLayout
